import { BLOG_LOCALES } from 'content-locales';

import { defaultLocale } from 'libs/locale';

import { LANDING_SLUGS } from 'landing-pages/localizedSlugs';

/**
 * TEMPORARY locale-availability guard for Header / Footer links.
 *
 * Landing pages are fully translated, so they resolve in every locale. CMS-driven pages are
 * being phased out and most have no it/es version yet — linking to one in a non-default locale
 * 404s. Until the CMS pages are gone, hide from the Header/Footer any link that would not
 * resolve in the active locale: keep landing pages always, and CMS pages only when they have a
 * confirmed localized version (the allowlist below). English (defaultLocale) is left as authored.
 *
 * When CMS pages disappear, this whole module (and its call sites) can be deleted.
 */
const LOCALIZED_CMS_ROUTES: ReadonlySet<string> = new Set(['api', 'pricing-products']);
const BLOG_LOCALE_SET: ReadonlySet<string> = new Set(BLOG_LOCALES);
const BLOG_ROUTES: ReadonlySet<string> = new Set(['blog', 'blog/news']);

/** Whether a Header/Footer link target resolves (does not 404) in the given locale. */
export const isRouteAvailable = (route: string | undefined | null, lang: string): boolean => {
  if (!route) return true; // non-link items (section headings, plain-text contact lines)
  if (lang === defaultLocale) return true; // English: show everything as authored
  if (route in LANDING_SLUGS) return true; // landing pages: fully translated in every locale
  if (BLOG_ROUTES.has(route)) return BLOG_LOCALE_SET.has(lang); // blog: only locales with a CMS blog
  return LOCALIZED_CMS_ROUTES.has(route); // other CMS pages: only those with a localized version
};

/** Drop unavailable items from menu columns, then drop any column left empty. */
export const filterColumnsByLocale = <T extends { navigation?: string }, C extends { items: T[] }>(
  columns: C[],
  lang: string,
): C[] =>
  columns
    .map((col) => ({ ...col, items: col.items.filter((item) => isRouteAvailable(item.navigation, lang)) }))
    .filter((col) => col.items.length > 0);
